import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Sparkles, Eye, BarChart3, Target, GitBranch } from "lucide-react";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

const STEPS = [
  { n: 1, icon: Search,     title: "Identify",  body: "A company, opportunity, or market you care about." },
  { n: 2, icon: Sparkles,   title: "Generate",  body: "AI intelligence — fast, structured, evidence-cited." },
  { n: 3, icon: Eye,        title: "Review",    body: "Fit, risks, and opportunity signals — all on one page." },
  { n: 4, icon: BarChart3,  title: "Score",     body: "Quantify the prospect or opportunity on a 100-point scale." },
  { n: 5, icon: Target,     title: "Act",       body: "Clear next actions and the decision maker to contact." },
  { n: 6, icon: GitBranch,  title: "Decide",    body: "Bid, team, pursue — or pass. With rationale." },
];

export default function HowItWorks() {
  useDocMeta({
    title: "How It Works · GovBidIQ",
    description: "Six steps from identifying an opportunity to a defensible bid/no-bid decision.",
    path: "/how-it-works",
  });
  React.useEffect(() => { track(EVENT.HOW_IT_WORKS_VIEWED, {}); }, []);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ How It Works</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-3">From opportunity → decision in six steps.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-10">
        GovBidIQ isn't a search engine and isn't a CRM. It's a senior-capture-manager's brain, on demand, every time.
      </p>

      <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-12">
        {STEPS.map(({ n, icon: Icon, title, body }) => (
          <div key={n} className="bg-[var(--bg-secondary)] p-6 flex gap-4" data-testid={`how-step-${n}`}>
            <div className="w-12 h-12 border border-sky-500/40 bg-sky-500/5 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-sky-400" strokeWidth={2} />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">Step {n}</div>
              <div className="font-display font-bold text-slate-100 text-lg mb-1">{title}</div>
              <div className="text-sm text-slate-400 leading-relaxed">{body}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-sky-500/30 bg-sky-500/5 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-slate-100 text-lg mb-1">Ready to see it in action?</div>
          <div className="text-sm text-slate-400">A three-minute guided tour, no signup required.</div>
        </div>
        <Link to="/tour" data-testid="how-cta-tour" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3">
          Take the Tour <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
